import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isFirefox, setIsFirefox] = useState(false);
  const [isFirefoxDesktop, setIsFirefoxDesktop] = useState(false);
  const [isSafariDesktop, setIsSafariDesktop] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Check if app is already installed (standalone mode)
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    setIsStandalone(isStandaloneMode);

    // Initial check for deferredPrompt
    if (window.deferredPrompt) {
      setDeferredPrompt(window.deferredPrompt);
      setShowPrompt(true);
      setCanInstall(true);
    }

    const handlePromptAvailable = () => {
      if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
        setShowPrompt(true);
        setCanInstall(true);
      }
    };

    window.addEventListener('pwa-prompt-available', handlePromptAvailable);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if Safari desktop (macOS)
    const safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const safariDesktop = safari && !iOS && !/Mobile|Android/i.test(navigator.userAgent);
    setIsSafariDesktop(safariDesktop);

    // Check if Firefox
    const firefox = navigator.userAgent.toLowerCase().includes('firefox');
    setIsFirefox(firefox);

    // Check if Firefox desktop (not mobile)
    const firefoxDesktop = firefox && !/Mobile|Android/i.test(navigator.userAgent);
    setIsFirefoxDesktop(firefoxDesktop);

    // Check if user previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Don't show if already installed or recently dismissed (1 day for testing)
    if (isStandaloneMode || daysSinceDismissed <= 1) {
      return;
    }

    // Check if browser supports PWA installation
    const checkInstallability = () => {
      // Check if service worker is supported (required for PWA)
      const hasServiceWorker = 'serviceWorker' in navigator;

      // Check if manifest is present
      const hasManifest = document.querySelector('link[rel="manifest"]') !== null;

      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isEdge = /Edg/.test(navigator.userAgent);
      const isChromium = isChrome || isEdge;

      // Chromium browsers support installation if service worker and manifest exist
      if (isChromium && hasServiceWorker && hasManifest && !isStandaloneMode) {
        return true;
      }

      // Firefox supports installation if service worker exists
      if (firefox && hasServiceWorker && !isStandaloneMode) {
        return true;
      }

      return false;
    };

    if (iOS) {
      // For iOS, show after a short delay
      const timer = setTimeout(() => {
        setShowPrompt(true);
        setCanInstall(false);
      }, 1500);
      return () => clearTimeout(timer);
    } else if (safariDesktop) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
        setCanInstall(false);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      // Fallback: show prompt after delay if event hasn't fired yet
      const timer = setTimeout(() => {
        if (!window.deferredPrompt) {
          const installable = checkInstallability();

          if (firefoxDesktop || firefox) {
            setShowPrompt(true);
            setCanInstall(false);
          } else if (installable) {
            setShowPrompt(true);
            setCanInstall(true);
          }
        }
      }, 3000);

      return () => {
        window.removeEventListener('pwa-prompt-available', handlePromptAvailable);
        clearTimeout(timer);
      };
    }
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt using the deferred prompt
      try {
        deferredPrompt.prompt();

        // Wait for the user to respond
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }

        // Clear the deferred prompt
        setDeferredPrompt(null);
        setShowPrompt(false);
        localStorage.setItem('pwa-install-dismissed', Date.now().toString());
      } catch (error) {
        console.error('Error showing install prompt:', error);
        // If prompt fails, try fallback method
        handleFallbackInstall();
      }
    } else if (canInstall) {
      // Fallback: try to trigger installation via other means
      handleFallbackInstall();
    }
  };

  const handleFallbackInstall = () => {
    // For browsers that support installation but don't have deferredPrompt,
    // we can try to guide the user or trigger browser-specific install UI

    // Check if we're in a Chromium browser
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(navigator.userAgent);

    if (isChrome || isEdge) {
      // Chrome/Edge: Show instructions to use the install icon in address bar
      // Look for the install icon in the address bar (usually appears automatically)
      // If not visible, guide user to menu
      const instructions = t('install.chromeInstructions') ||
        'Look for the install icon (⊕) in your browser\'s address bar. If you don\'t see it, click the menu (⋮) and select "Install app" or "Install Ynskje".';

      // Show instructions using toast notification
      toast.success(instructions, {
        duration: 6000,
        style: {
          maxWidth: '400px',
        }
      });
    } else {
      // For other browsers, show generic instructions
      const instructions = t('install.genericInstructions') ||
        'Please use your browser\'s menu to install this app. Look for an "Install" or "Add to Home Screen" option.';

      toast.info(instructions, {
        duration: 6000,
        style: {
          maxWidth: '400px',
        }
      });
    }

    // Don't dismiss the prompt, let user try again
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if already installed or prompt shouldn't be shown
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50 animate-slide-up">
      <div className="bg-white rounded-lg shadow-2xl border-2 border-primary-500 p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-100 rounded-lg p-2">
              <Download className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">
                {t('install.title')}
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                {t('install.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        {isIOS ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {t('install.iosInstructions')}
            </p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  1
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.iosStep1')}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  2
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.iosStep2')}
                </p>
              </div>
            </div>
          </div>
        ) : isSafariDesktop ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {t('install.safariDesktopInstructions')}
            </p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  1
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.safariDesktopStep1')}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  2
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.safariDesktopStep2')}
                </p>
              </div>
            </div>
          </div>
        ) : isFirefoxDesktop ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {t('install.firefoxDesktopInstructions')}
            </p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  1
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.firefoxDesktopStep1')}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  2
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.firefoxDesktopStep2')}
                </p>
              </div>
            </div>
          </div>
        ) : isFirefox ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              {t('install.firefoxInstructions')}
            </p>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  1
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.firefoxStep1')}
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="flex-shrink-0 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                  2
                </span>
                <p className="text-sm text-gray-700 flex-1">
                  {t('install.firefoxStep2')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-700 mb-4">
            {t('install.description')}
          </p>
        )}

        {/* Actions */}
        <div className="flex space-x-2 mt-4">
          {/* Show install button if we have deferredPrompt OR if canInstall is true (even without deferredPrompt) */}
          {!isIOS && !isFirefox && !isSafariDesktop && (deferredPrompt || canInstall) && (
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Download className="h-4 w-4" />
              <span>{t('install.installButton')}</span>
            </button>
          )}
          <button
            onClick={handleDismiss}
            className={(!isIOS && !isFirefox && !isSafariDesktop && (deferredPrompt || canInstall)) ? "px-4 py-2.5 text-gray-700 hover:text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors" : "w-full px-4 py-2.5 text-gray-700 hover:text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors"}
          >
            {t('install.laterButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;

